import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import styled from '@emotion/styled';
import { Collapse, List, ListItem as MenuItem, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import {
  AddCircle as AddCircleIcon,
  ExpandLess,
  ExpandMore,
  FiberManualRecordRounded as FiberManualRecordRoundedIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { FunctionComponent, PropsWithChildren, useState } from 'react';

import { useExtendedIntl } from 'hooks/useExtendedIntl';
import { APIPermission } from 'typeDeclarations/enums';
import { PermissionsViewDecider } from 'shared/PermissionsViewDecider';
import { hashNumberFromString, teamsColorPalette } from './utils';
import { CreateTeamDialog } from './CreateTeamDialog/CreateTeamDialog';
import { ErrorMessage } from 'shared/ErrorMessage';
import { TeamNode, UserNode } from 'typeDeclarations/graphql/nodes';
import { NonPaginatedConnection } from 'typeDeclarations/graphql/base-types';

export const NUM_INITIAL_TEAMS = 3;

const StyledFiberManualRecordRoundedIcon = styled(FiberManualRecordRoundedIcon)`
  transform: scale(0.5);
`;

/**
 * Fragment and type created for the optimization purpose during the login process
 */
export interface TeamsMenuQueryFragmentData {
  sessionUser: Pick<UserNode, 'id' | 'modified' | 'numTeams' | 'isStaff'> & {
    teams: NonPaginatedConnection<Pick<TeamNode, '__typename' | '_id' | 'id' | 'modified' | 'name'>>;
  };
}

export const TEAMS_MENU_QUERY_FRAGMENT = gql(/* GraphQL */ `
  fragment TeamsMenuQueryFragment on Query {
    sessionUser {
      id
      modified
      numTeams
      isStaff
      teams(first: $teamsAmount, orderBy: "name") {
        edges {
          node {
            id
            _id
            modified
            name
          }
        }
      }
    }
  }
`);

type TeamsMenuQueryData = TeamsMenuQueryFragmentData;

interface TeamsMenuQueryVariables {
  teamsAmount: number;
}

const TEAMS_MENU_QUERY = gql(/* GraphQL */ `
  query teamsMenuQuery($teamsAmount: Int!) {
    ...TeamsMenuQueryFragment
  }
`);

interface TeamsMenuProps {
  onMoreTeamsClick: () => void;
  onTeamClick: (teamId: string) => void;
}

export const TeamsMenu: FunctionComponent<PropsWithChildren<TeamsMenuProps>> = ({ onMoreTeamsClick, onTeamClick }) => {
  const { formatMessage } = useExtendedIntl();
  const [open, setOpen] = useState(false);
  const [openCreateNewTeamDialog, setOpenCreateNewTeamDialog] = useState(false);

  const handleCreateNewTeamDialogClose = () => setOpenCreateNewTeamDialog(false);

  const handleCreateNewTeamDialogOpen = () => setOpenCreateNewTeamDialog(true);

  const theme = useTheme();

  const handleClick = () => setOpen(!open);

  const { loading, error, data } = useQuery<TeamsMenuQueryData, TeamsMenuQueryVariables>(TEAMS_MENU_QUERY, {
    variables: {
      teamsAmount: NUM_INITIAL_TEAMS,
    },
  });

  if (loading) {
    return null;
  }

  if (error || !data) {
    return <ErrorMessage />;
  }

  const { teams, isStaff, numTeams } = data.sessionUser;

  if (!numTeams || numTeams < 2) {
    return null;
  }

  return (
    <>
      <MenuItem button onClick={handleClick} data-testid="menu-select-teams">
        <ListItemIcon>
          <PeopleIcon htmlColor={theme.palette.text.primary} />
        </ListItemIcon>
        <ListItemText>{formatMessage({ id: 'teams-menu.list-item-text' })}</ListItemText>
        {open ? <ExpandLess /> : <ExpandMore />}
      </MenuItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding>
          {isStaff && (
            <PermissionsViewDecider requiredPermissions={[APIPermission.PerformChanges]}>
              <>
                <MenuItem button onClick={handleCreateNewTeamDialogOpen}>
                  <ListItemIcon>
                    <AddCircleIcon htmlColor={theme.palette.text.primary} />
                  </ListItemIcon>
                  <Typography variant="body1">{formatMessage({ id: 'create-team-button.title' })}</Typography>
                </MenuItem>
                <CreateTeamDialog open={openCreateNewTeamDialog} onClose={handleCreateNewTeamDialogClose} />
              </>
            </PermissionsViewDecider>
          )}
          {teams.edges.map(({ node: team }) => {
            const teamColorIndex = hashNumberFromString(team._id) % teamsColorPalette.length;
            const teamColor = teamsColorPalette[teamColorIndex];

            return (
              <MenuItem
                button
                key={team.id}
                onClick={(e) => {
                  e.preventDefault();
                  onTeamClick(team.id);
                }}
              >
                <ListItemIcon>
                  <StyledFiberManualRecordRoundedIcon htmlColor={teamColor} />
                </ListItemIcon>
                <ListItemText primary={team.name} />
              </MenuItem>
            );
          })}
          {/* Is the total number of teams greater than number of listed teams? */}
          {numTeams > NUM_INITIAL_TEAMS && (
            <MenuItem button onClick={onMoreTeamsClick}>
              <ListItemIcon />
              <ListItemText
                primary={`${numTeams - NUM_INITIAL_TEAMS}${formatMessage({
                  id: 'teams-submenu.menu-item.primary-text',
                })}`}
                secondary={formatMessage({ id: 'teams-submenu.menu-item.secondary-text' })}
              />
            </MenuItem>
          )}
        </List>
      </Collapse>
    </>
  );
};
